<template>
  <div
    class="input__container"
    :class="styleAttrs.class"
    :style="styleAttrs.style"
  >
    <div v-if="label || $slots.label" class="input__before">
      <slot name="label">
        <label
          v-if="label"
          class="label input__label"
          :for="id"
        >
          {{ label }}
        </label>
      </slot>
      <slot name="top-right"></slot>
    </div>
    <div
      class="input input-autogrowarea input-autogrowarea--oneline"
      :class="{ 'has-error': hasError }"
      @click="focus()"
    >
      <div class="input__element input-autogrowarea__sizer">
        {{ value + "  " }}
      </div>
      <input
        :id="id"
        ref="inputElement"
        v-model="currentNumber"
        class="input__element input_number input-autogrowarea__input"
        type="number"
        :class="[...inputClass, { 'is-not-editable': !editable }]"
        :step="step"
        :max="max ?? undefined"
        :min="min ?? undefined"
        :placeholder="placeholder != null ? placeholder + '' : ''"
        :readonly="!editable"
        :required="!!functionalAttrs?.required && !allowNull"
        v-bind="functionalAttrs"
        @input="onInput"
        @change="onChange"
        @keydown.enter="onEnter"
        @wheel.passive="onWheel"
        @blur="emit('blur', $event)"
      />
      <div class="input__buttons">
        <button
          v-for="icon in icons"
          :key="icon"
          type="button"
          class="button-icon input__icon"
          @click="onIconClick($event, icon)"
        >
          <lucide-icon :name="icon" />
        </button>
        <slot name="buttons"></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="N extends boolean = false">
import { v4 as uuid } from "uuid";
import { nextTick, ref, watch, watchEffect } from "vue";
import { useAttrsAndStyle } from "@core/composables/attrsAndStyle";
import type { IconName } from "@core/ui/lucideIcons";

import LucideIcon from "@core/ui/LucideIcon.vue";

// TODO: Extend value checking depending on allowNull prop (if allowNull is true, then value can also be null, otherwise it can't)

defineOptions({
  inheritAttrs: false
});

type ValueType = N extends true ? number | null : number;

interface Props {
  id?: string;
  value?: number | null;
  min?: number | null;
  max?: number | null;
  step?: number | string;
  placeholder?: string | number | null;
  editable?: boolean;
  inputClass?: string[] | { [key: string]: boolean }[];
  icons?: IconName[];
  label?: string | null;
  allowNull?: N;
}
const props = withDefaults(defineProps<Props>(), {
  id: () => `input-${uuid()}`,
  value: null,
  min: 0,
  max: Infinity,
  step: 1,
  placeholder: null,
  editable: true,
  inputClass: () => [],
  icons: () => [],
  label: null,
  allowNull: false as any
});

const emit = defineEmits<{
  (e: "update:value", value: ValueType): void;
  (e: "enter", keyEvent: KeyboardEvent): void;
  (e: "change", value: ValueType): void;
  (e: `click:${IconName}`, mouseEvent: MouseEvent): void;
  (e: "blur", event: FocusEvent): void;
}>();

const inputElement = ref<HTMLInputElement>();
const currentNumber = ref<number | string | null>(0);
const hasError = ref(false);
const { functionalAttrs, styleAttrs } = useAttrsAndStyle();

/**
 * Always update the local value when the value prop changes:
 */
watchEffect(() => {
  currentNumber.value = props.value;
});

watch([() => props.min, () => props.max], () => {
  nextTick(() => {
    if (inputElement.value) {
      const valid = inputElement.value.checkValidity();
      if (!valid) {
        hasError.value = true;
      } else {
        hasError.value = false;
      }
    }
  });
});

/**
 * Handle Input events on the main input element
 */
function onInput(event: Event) {
  event.preventDefault();

  let number: number | null = 0;

  if (currentNumber.value === "" && props.allowNull) {
    number = null;
    currentNumber.value = null;
  } else {
    number = parseNum(currentNumber.value);
    if (isNaN(number)) {
      hasError.value = true;
      return;
    }
    hasError.value = false;
    currentNumber.value = number;
  }
  // Mark the keyup event as handled.
  // This ensures that no other part of the software
  // does anything with that key event.
  // (On backspace, not only did a number get deleted, but
  // the corresponding element in the net was also removed)
  if (number === 0) {
    window.addEventListener(
      "keyup",
      (event) => {
        event.preventDefault();
      },
      {
        once: true,
        capture: true
      }
    );
  }
  if (inputElement.value?.checkValidity?.()) {
    emit("update:value", number as ValueType);
  } else {
    hasError.value = true;
    console.warn("can't save", number);
  }
}

/**
 * Handle the change event
 */
function onChange() {
  const input = inputElement.value;
  const min = props.min ?? -Infinity;
  const max = props.max ?? Infinity;

  if (!props.allowNull && !currentNumber.value && min <= 0) {
    currentNumber.value = 0;
    if (input) {
      input.value = "0";
    }
  }
  if (input) {
    if (input.reportValidity()) {
      emit("change", parseNum(currentNumber.value) as ValueType);
      emit("update:value", parseNum(currentNumber.value) as ValueType);
    } else {
      hasError.value = true;
    }
  } else {
    if (currentNumber.value == null) {
      if (props.allowNull) {
        emit("update:value", null as ValueType);
        emit("change", null as ValueType);
      } else {
        hasError.value = true;
      }
    } else {
      const num = parseNum(currentNumber.value);
      if (!isNaN(num) && num >= min && num <= max) {
        emit("update:value", num as ValueType);
        emit("change", num as ValueType);
      } else {
        hasError.value = true;
      }
    }
  }
}

/**
 * Parse a number to int or float (depending on prop)
 */
function parseNum(value: string | number | null) {
  if (typeof value === "number") {
    return value;
  }
  if (value == null) value = "";
  return parseFloat(value);
}

/**
 * Focus the input
 */
function focus() {
  if (inputElement.value == null) return;
  inputElement.value.focus();
}

function onEnter(keyEvent: KeyboardEvent) {
  emit("enter", keyEvent);
}
function onIconClick(mouseEvent: MouseEvent, icon: IconName) {
  mouseEvent.preventDefault();
  emit(`click:${icon}`, mouseEvent);
}
/**
 * Prevent changing the value with the mouse wheel.
 */
function onWheel(event: WheelEvent) {
  const eventTarget = event.target as HTMLInputElement;
  eventTarget.blur();
}

defineExpose({
  focus
});
</script>
